import React from 'react';

import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { TenantSettingKey } from '@recurrency/core-api-schema/dist/common/tenantSettings';
import moment from 'moment';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import { theme } from 'theme';

import { useCoreApi } from 'hooks/useApi';

import { getTenantSetting } from 'utils/tenantSettings';

import { ChartTooltip } from '../shared/ChartTooltip';
import { useInventoryReportFilters } from '../useInventoryReportFilters';
import { MetricsCard } from './MetricsCard';

type RiskLevel = 'Low' | 'Healthy' | 'High';

const DEFAULT_HIGH_THRESHOLD = 180;
const DEFAULT_LOW_THRESHOLD = 90;

interface RiskInfo {
  level: RiskLevel;
  lineColor: string;
  textColor: string;
  bgColor: string;
}

function getRiskInfo(days: number, lowThreshold: number, highThreshold: number): RiskInfo {
  if (days < lowThreshold) {
    return {
      level: 'Low',
      lineColor: theme.colors.warning[500],
      textColor: theme.colors.warning[600],
      bgColor: theme.colors.warning[200],
    };
  }
  if (days <= highThreshold) {
    return {
      level: 'Healthy',
      lineColor: theme.colors.success[500],
      textColor: theme.colors.success[600],
      bgColor: theme.colors.success[200],
    };
  }
  return {
    level: 'High',
    lineColor: theme.colors.danger[500],
    textColor: theme.colors.danger[600],
    bgColor: theme.colors.danger[200],
  };
}

export function InventoryAverageAgeChart() {
  const reportFilters = useInventoryReportFilters();

  const lowThreshold =
    getTenantSetting(TenantSettingKey.CalculationAgeOfInventoryLowThreshold) ?? DEFAULT_LOW_THRESHOLD;
  // Clamp the high threshold so it can never fall below the low threshold (otherwise the colored bands
  // overlap and the legend range reads backwards).
  const highThreshold = Math.max(
    lowThreshold,
    getTenantSetting(TenantSettingKey.CalculationAgeOfInventoryHighThreshold) ?? DEFAULT_HIGH_THRESHOLD,
  );

  const {
    data: averageAgeData,
    isLoading,
    isReloading,
  } = useCoreApi(schemas.inventory.getInventoryAverageAge, { queryParams: { filters: reportFilters } });

  const items = averageAgeData?.items ?? [];
  const latestValue = items.length > 0 ? items[items.length - 1].averageAgeDays : 0;
  const latestRisk = getRiskInfo(latestValue, lowThreshold, highThreshold);

  const maxY = Math.max(highThreshold + 20, ...items.map((d) => d.averageAgeDays));
  const yDomainMax = Math.ceil(maxY / 10) * 10 + 10;

  const chartData = items.map((d) => ({
    date: moment(d.date).format('MMM YY'),
    averageAgeDays: d.averageAgeDays,
  }));

  return (
    <MetricsCard
      title="Average Inventory Age"
      description="The quantity-weighted average age of on-hand inventory at the start of each month, assuming FIFO consumption and LIFO-ordered receipt layers. Thresholds can be adjusted through settings."
      loading={isLoading || isReloading}
      headerActions={
        <div
          className={css`
            display: flex;
            align-items: center;
            gap: 10px;
          `}
        >
          <span
            className={css`
              font-weight: 700;
              font-size: 18px;
              color: ${theme.colors.neutral[600]};
            `}
          >
            Current:
          </span>
          <span
            className={css`
              font-weight: 700;
              font-size: 18px;
              color: ${latestRisk.textColor};
            `}
          >
            {Math.round(latestValue)} days
          </span>
          <span
            className={css`
              display: inline-flex;
              align-items: center;
              padding: 2px 10px;
              border-radius: 12px;
              font-size: 12px;
              font-weight: 600;
              background-color: ${latestRisk.bgColor};
              color: ${latestRisk.textColor};
            `}
          >
            {latestRisk.level}
          </span>
        </div>
      }
    >
      <>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={chartData} margin={{ left: -1 }}>
            <defs>
              <linearGradient id="fillAverageAge" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={theme.colors.chart.sunsetPurple} stopOpacity={0.3} />
                <stop offset="95%" stopColor={theme.colors.chart.sunsetPurple} stopOpacity={0} />
              </linearGradient>
            </defs>

            <XAxis
              dataKey="date"
              tick={{ fill: theme.colors.primary[600], stroke: theme.colors.primary[600], strokeWidth: 0.2 }}
              interval={2}
            />
            <YAxis
              domain={[0, yDomainMax]}
              tickFormatter={(v) => `${v}d`}
              tick={{ fill: theme.colors.primary[600], stroke: theme.colors.primary[600], strokeWidth: 0.2 }}
            />
            <CartesianGrid strokeDasharray="3 3" />

            {/* Risk range bands — rendered before the area so they appear behind */}
            <ReferenceArea y1={0} y2={lowThreshold} fill={theme.colors.warning[200]} fillOpacity={0.5} />
            <ReferenceArea y1={lowThreshold} y2={highThreshold} fill={theme.colors.success[200]} fillOpacity={0.5} />
            <ReferenceArea y1={highThreshold} y2={yDomainMax} fill={theme.colors.danger[200]} fillOpacity={0.5} />

            <Area
              type="linear"
              dataKey="averageAgeDays"
              stroke={theme.colors.chart.sunsetPurple}
              strokeWidth={2}
              dot={false}
              fill="url(#fillAverageAge)"
            />

            <Tooltip
              content={(e: TooltipProps) => {
                if (e.active && e.payload?.length) {
                  const val = e.payload[0].value as number;
                  const r = getRiskInfo(val, lowThreshold, highThreshold);
                  return (
                    <ChartTooltip borderColor={theme.colors.chart.sunsetPurple}>
                      <div>{moment(e.payload[0].payload.date, 'MMM YY').format('MMMM YYYY')}</div>
                      <div>{Math.round(val)} days</div>
                      <div
                        className={css`
                          color: ${r.textColor};
                          font-weight: 600;
                        `}
                      >
                        {r.level}
                      </div>
                    </ChartTooltip>
                  );
                }
                return null;
              }}
            />
          </AreaChart>
        </ResponsiveContainer>

        {/* Range legend */}
        <div
          className={css`
            display: flex;
            gap: 20px;
            justify-content: center;
            padding: 4px 0 8px;
          `}
        >
          {(
            [
              {
                level: 'Low',
                label: `< ${lowThreshold} days`,
                bgColor: theme.colors.warning[200],
                textColor: theme.colors.warning[700],
              },
              {
                level: 'Healthy',
                label: `${lowThreshold}–${highThreshold} days`,
                bgColor: theme.colors.success[200],
                textColor: theme.colors.success[700],
              },
              {
                level: 'High',
                label: `> ${highThreshold} days`,
                bgColor: theme.colors.danger[200],
                textColor: theme.colors.danger[700],
              },
            ] as const
          ).map(({ level, label, bgColor, textColor }) => (
            <div
              key={level}
              className={css`
                display: flex;
                align-items: center;
                gap: 6px;
                font-size: 12px;
                color: ${theme.colors.neutral[600]};
              `}
            >
              <span
                className={css`
                  display: inline-block;
                  width: 12px;
                  height: 12px;
                  border-radius: 2px;
                  background-color: ${bgColor};
                  border: 1px solid ${textColor};
                `}
              />
              <strong
                className={css`
                  color: ${textColor};
                `}
              >
                {level}
              </strong>
              {label}
            </div>
          ))}
        </div>
      </>
    </MetricsCard>
  );
}
